import { Component, h, escapeHtml, register, registerFieldType } from './core.js';
import { renderIcon } from './icons.js';

/**
 * RichText: an HTML editor with a configurable toolbar.
 *
 * new dfc.RichText(container, {
 *   value: '<p></p>', placeholder: 'Start writing…', toolbar: 'basic' | 'html' | 'rich' | [items],
 *   minHeight: 160, maxHeight: null, readOnly: false, disabled: false,
 *   maxImageSize: 512000, onImage: null | (file) => Promise<src>,   // return a src to use instead of a data URL
 *   linkTarget: '_blank', colors: [...], headings: [['p','Paragraph'],['h1','Heading 1'],['h2','Heading 2'],['h3','Heading 3']],
 *   allowedTags: null (override the whitelist), imageSrcPattern: /^(data:image\/|https?:|\/)/
 * })
 * Toolbar items: bold italic underline strike color lowercase uppercase heading alignLeft alignCenter alignRight
 *                ul ol outdent indent link image clear source undo redo  and '|' for a separator.
 * Presets: basic (formatting, case, alignment, lists, undo/redo), html (basic + heading, link, clear, source),
 *          rich (html + image).
 * Value: the sanitised HTML. Events: valueChanged, focus, blur, imageRejected { file, reason }, command { name }.
 * Methods: getText(), insertHtml(html), insertText(text), exec(name, arg), focus(), toggleSource(on)
 */
const PRESETS = {
	basic: ['bold', 'italic', 'underline', 'color', 'lowercase', 'uppercase', '|', 'alignLeft', 'alignCenter', 'alignRight', 'ul', 'ol', 'outdent', 'indent', '|', 'undo', 'redo'],
	html: ['bold', 'italic', 'underline', 'strike', 'color', 'lowercase', 'uppercase', '|', 'heading', 'alignLeft', 'alignCenter', 'alignRight', 'ul', 'ol', 'outdent', 'indent', '|', 'link', 'clear', 'source', '|', 'undo', 'redo'],
	rich: ['bold', 'italic', 'underline', 'strike', 'color', 'lowercase', 'uppercase', '|', 'heading', 'alignLeft', 'alignCenter', 'alignRight', 'ul', 'ol', 'outdent', 'indent', '|', 'link', 'image', 'clear', 'source', '|', 'undo', 'redo'],
};

const ITEMS = {
	bold: { icon: 'bold', title: 'Bold (Ctrl+B)', cmd: 'bold', state: 'bold' },
	italic: { icon: 'italic', title: 'Italic (Ctrl+I)', cmd: 'italic', state: 'italic' },
	underline: { icon: 'underline', title: 'Underline (Ctrl+U)', cmd: 'underline', state: 'underline' },
	strike: { icon: 'strike', title: 'Strikethrough', cmd: 'strikeThrough', state: 'strikeThrough' },
	color: { icon: 'palette', title: 'Text colour', popover: 'color' },
	lowercase: { text: 'ab', title: 'lower case', action: 'lowercase' },
	uppercase: { text: 'AB', title: 'UPPER CASE', action: 'uppercase' },
	heading: { select: 'heading', title: 'Paragraph style' },
	alignLeft: { icon: 'align-left', title: 'Align left', cmd: 'justifyLeft', state: 'justifyLeft' },
	alignCenter: { icon: 'align-center', title: 'Align centre', cmd: 'justifyCenter', state: 'justifyCenter' },
	alignRight: { icon: 'align-right', title: 'Align right', cmd: 'justifyRight', state: 'justifyRight' },
	ul: { icon: 'list-ul', title: 'Bulleted list', cmd: 'insertUnorderedList', state: 'insertUnorderedList' },
	ol: { icon: 'list-ol', title: 'Numbered list', cmd: 'insertOrderedList', state: 'insertOrderedList' },
	outdent: { icon: 'outdent', title: 'Decrease indent', cmd: 'outdent' },
	indent: { icon: 'indent', title: 'Increase indent', cmd: 'indent' },
	link: { icon: 'link', title: 'Link (Ctrl+K)', popover: 'link' },
	image: { icon: 'image', title: 'Insert image', action: 'image' },
	clear: { icon: 'eraser', title: 'Clear formatting', cmd: 'removeFormat' },
	source: { icon: 'code', title: 'HTML source', action: 'source' },
	undo: { icon: 'undo', title: 'Undo (Ctrl+Z)', action: 'undo' },
	redo: { icon: 'redo', title: 'Redo (Ctrl+Y)', action: 'redo' },
};

const ALLOWED = {
	p: ['style', 'class'], br: [], h1: ['style', 'id'], h2: ['style', 'id'], h3: ['style', 'id'], h4: ['style', 'id'], blockquote: ['style'], pre: [], code: [],
	b: [], strong: [], i: [], em: [], u: [], s: [], strike: [], sub: [], sup: [], span: ['style', 'class', 'data-comment-id'], font: ['color'],
	ins: ['class', 'data-change-id', 'data-author', 'data-at'], del: ['class', 'data-change-id', 'data-author', 'data-at'],
	ul: [], ol: ['start'], li: ['style', 'class'], a: ['href', 'target', 'rel', 'title'], img: ['src', 'alt', 'width', 'height', 'style'], hr: ['class'], nav: ['class'],
	div: ['style'], table: [], thead: [], tbody: [], tr: [], th: ['style'], td: ['style'],
};
const STYLE_ALLOW = { color: /^(#[0-9a-f]{3,8}|rgb\(|rgba\(|[a-z]+$)/i, 'background-color': /^(#[0-9a-f]{3,8}|rgb\(|rgba\(|[a-z]+$)/i, 'text-align': /^(left|center|right|justify)$/, 'max-width': /^\d+(px|%)$/, width: /^\d+(px|%)$/, height: /^\d+(px|%)$/, 'text-decoration': /^(underline|line-through)$/, 'font-weight': /^(bold|\d{3})$/, 'font-style': /^italic$/ };

export class RichText extends Component {
	static get componentName() { return 'richtext'; }

	defaults() {
		return {
			value: '', placeholder: 'Start writing…', toolbar: 'basic', minHeight: 160, maxHeight: null, readOnly: false, disabled: false,
			maxImageSize: 512000, onImage: null, linkTarget: '_blank', imageSrcPattern: /^(data:image\/|https?:\/\/|\/)/i,
			colors: ['#1f2937', '#6b7280', '#dc2626', '#f59e0b', '#16a34a', '#2563eb', '#7c3aed', '#db2777', '#0f766e', '#ffffff'],
			headings: [['p', 'Paragraph'], ['h1', 'Heading 1'], ['h2', 'Heading 2'], ['h3', 'Heading 3'], ['blockquote', 'Quote']],
			allowedTags: null, sanitizePaste: true,
		};
	}

	render() {
		const o = this.options;
		const firstRender = !this.editor;
		const current = firstRender ? RichText.sanitize(o.value || '', this._allowed(), this._sanOpts()) : this.value;
		this.el.className = 'dfc dfc-richtext' + (o.readOnly ? ' is-readonly' : '') + (o.disabled ? ' is-disabled' : '') + (o.className ? ' ' + o.className : '');
		this.el.innerHTML = '';
		this._history = this._history || { stack: [current], index: 0 };
		this.toolbar = h('div', { className: 'dfc-richtext-toolbar', role: 'toolbar', 'aria-label': 'Formatting' });
		this._buildToolbar();
		this.el.appendChild(this.toolbar);
		this.popover = h('div', { className: 'dfc-richtext-popover', hidden: true });
		this.el.appendChild(this.popover);
		this.editor = h('div', {
			className: 'dfc-richtext-editor', contenteditable: o.readOnly || o.disabled ? 'false' : 'true', role: 'textbox', 'aria-multiline': 'true',
			'aria-label': o.ariaLabel || 'Rich text', 'data-placeholder': o.placeholder, style: { minHeight: o.minHeight + 'px', maxHeight: o.maxHeight ? o.maxHeight + 'px' : null },
		});
		this.editor.innerHTML = current || '';
		this.source = h('textarea', { className: 'dfc-richtext-source', hidden: true, 'aria-label': 'HTML source', spellcheck: 'false' });
		this.el.appendChild(this.editor);
		this.el.appendChild(this.source);
		this.fileInput = h('input', { type: 'file', accept: 'image/*', className: 'dfc-richtext-file', tabindex: '-1', 'aria-hidden': 'true' });
		this.el.appendChild(this.fileInput);
		this._sourceMode = false;
		this._refreshStates();
	}

	_items() {
		const t = this.options.toolbar;
		return Array.isArray(t) ? t : (PRESETS[t] || PRESETS.basic);
	}

	_buildToolbar() {
		const o = this.options;
		this.buttons = {};
		this._items().forEach((name) => {
			if (name === '|') { this.toolbar.appendChild(h('span', { className: 'dfc-richtext-sep', role: 'separator' })); return; }
			const def = ITEMS[name];
			if (!def) return;
			if (def.select === 'heading') {
				const sel = h('select', { className: 'dfc-richtext-select', title: def.title, 'aria-label': def.title, dataset: { item: name } },
					...o.headings.map(([tag, label]) => h('option', { value: tag, text: label })));
				this.buttons[name] = sel;
				this.toolbar.appendChild(sel);
				return;
			}
			const btn = h('button', { type: 'button', className: 'dfc-richtext-btn', title: def.title, 'aria-label': def.title, dataset: { item: name }, 'aria-pressed': def.state ? 'false' : null },
				def.icon ? renderIcon(def.icon) : h('span', { className: 'dfc-richtext-btntext', text: def.text }));
			this.buttons[name] = btn;
			this.toolbar.appendChild(btn);
		});
	}

	bind() {
		this._onToolbar = (e) => {
			const btn = e.target.closest && e.target.closest('[data-item]');
			if (!btn || !this.toolbar.contains(btn) || btn.tagName === 'SELECT') return;
			e.preventDefault();
			this._run(btn.dataset.item);
		};
		this._onToolbarDown = (e) => { const b = e.target.closest && e.target.closest('.dfc-richtext-btn'); if (b) e.preventDefault(); }; // keep editor selection
		this._onSelect = (e) => { if (e.target.dataset && e.target.dataset.item === 'heading') { this._restoreSelection(); this.exec('formatBlock', e.target.value); } };
		this._onInput = () => { this._afterEdit('user'); };
		this._onKey = (e) => {
			if (!(e.ctrlKey || e.metaKey)) return;
			const k = e.key.toLowerCase();
			if (k === 'z' && !e.shiftKey) { e.preventDefault(); this._run('undo'); }
			else if (k === 'y' || (k === 'z' && e.shiftKey)) { e.preventDefault(); this._run('redo'); }
			else if (k === 'k' && this.buttons.link) { e.preventDefault(); this._run('link'); }
		};
		this._onPaste = (e) => {
			if (!this.options.sanitizePaste || !e.clipboardData) return;
			const html = e.clipboardData.getData('text/html');
			const text = e.clipboardData.getData('text/plain');
			if (!html && !text) return;
			e.preventDefault();
			const clean = html ? RichText.sanitize(html, this._allowed(), { ...this._sanOpts(), stripStyles: true }) : escapeHtml(text).replace(/\r?\n/g, '<br>');
			this.exec('insertHTML', clean);
		};
		this._onSelChange = () => { if (this.editor && document.activeElement === this.editor) { this._saveSelection(); this._refreshStates(); } };
		this._onFocus = () => this.emit('focus', {});
		this._onBlur = () => { this._saveSelection(); this.emit('blur', {}); };
		this._onFile = () => { const f = this.fileInput.files && this.fileInput.files[0]; this.fileInput.value = ''; if (f) this._insertImageFile(f); };
		this._onSourceInput = () => { this._sourceDirty = true; };
		this._onDocClick = (e) => { if (this.popover && !this.popover.hidden && !this.popover.contains(e.target) && !this.toolbar.contains(e.target)) this._closePopover(); };
		this.toolbar.addEventListener('click', this._onToolbar);
		this.toolbar.addEventListener('mousedown', this._onToolbarDown);
		this.toolbar.addEventListener('change', this._onSelect);
		this.editor.addEventListener('input', this._onInput);
		this.editor.addEventListener('keydown', this._onKey);
		this.editor.addEventListener('paste', this._onPaste);
		this.editor.addEventListener('focus', this._onFocus);
		this.editor.addEventListener('blur', this._onBlur);
		this.fileInput.addEventListener('change', this._onFile);
		this.source.addEventListener('input', this._onSourceInput);
		document.addEventListener('selectionchange', this._onSelChange);
		document.addEventListener('mousedown', this._onDocClick, true);
	}

	unbind() {
		document.removeEventListener('selectionchange', this._onSelChange);
		document.removeEventListener('mousedown', this._onDocClick, true);
	}

	// ------------------------------------------------------------ commands
	_run(name) {
		const def = ITEMS[name];
		if (!def || this.options.readOnly || this.options.disabled) return;
		if (name !== 'source' && this._sourceMode) this.toggleSource(false);
		this._restoreSelection();
		if (def.popover) { this._openPopover(def.popover, this.buttons[name]); return; }
		if (def.cmd) { this.exec(def.cmd); return; }
		if (def.action === 'undo') this.undo();
		else if (def.action === 'redo') this.redo();
		else if (def.action === 'source') this.toggleSource();
		else if (def.action === 'image') this.fileInput.click();
		else if (def.action === 'lowercase' || def.action === 'uppercase') this._transformCase(def.action === 'uppercase');
		this.emit('command', { name });
	}

	/** Runs an editing command on the editor (a thin layer over the browser's editing engine). */
	exec(command, arg) {
		if (this.options.readOnly || this.options.disabled) return this;
		this.editor.focus();
		this._restoreSelection();
		try { document.execCommand('styleWithCSS', false, command === 'foreColor'); } catch (e) { /* ignore */ }
		let ok = false;
		try { ok = document.execCommand(command, false, arg === undefined ? null : arg); } catch (e) { ok = false; }
		if (!ok && command === 'formatBlock') { try { document.execCommand('formatBlock', false, '<' + arg + '>'); } catch (e) { /* ignore */ } }
		this._afterEdit('user');
		this.emit('command', { name: command });
		return this;
	}

	insertHtml(html) { return this.exec('insertHTML', RichText.sanitize(html, this._allowed(), this._sanOpts())); }
	insertText(text) { return this.exec('insertText', String(text)); }

	_transformCase(upper) {
		const sel = window.getSelection();
		if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
		const text = sel.toString();
		this.exec('insertText', upper ? text.toUpperCase() : text.toLowerCase());
	}

	async _insertImageFile(file) {
		const o = this.options;
		if (!/^image\//.test(file.type)) { this.emit('imageRejected', { file, reason: 'type' }); return; }
		if (o.maxImageSize && file.size > o.maxImageSize) { this.emit('imageRejected', { file, reason: 'size', limit: o.maxImageSize }); return; }
		let src;
		try {
			src = typeof o.onImage === 'function' ? await o.onImage(file) : await RichText.readAsDataUrl(file);
		} catch (e) { this.emit('imageRejected', { file, reason: 'error', message: e.message }); return; }
		if (!src) return;
		this.exec('insertHTML', `<img src="${escapeHtml(src)}" alt="${escapeHtml(file.name)}" style="max-width:100%">`);
	}

	static readAsDataUrl(file) {
		return new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = () => reject(r.error || new Error('read failed')); r.readAsDataURL(file); });
	}

	// ------------------------------------------------------------ popovers (colour, link)
	_openPopover(kind, anchor) {
		const o = this.options;
		this.popover.innerHTML = '';
		this.popover.hidden = false;
		this.popover.style.left = Math.max(0, anchor.offsetLeft) + 'px';
		if (kind === 'color') {
			const grid = h('div', { className: 'dfc-richtext-swatches' }, ...o.colors.map((c) => h('button', { type: 'button', className: 'dfc-richtext-swatch', title: c, style: { background: c }, dataset: { color: c } })));
			const custom = h('input', { type: 'color', className: 'dfc-richtext-colorinput', 'aria-label': 'Custom colour' });
			custom.addEventListener('input', () => { this._restoreSelection(); this.exec('foreColor', custom.value); });
			grid.addEventListener('click', (e) => { const s = e.target.closest('[data-color]'); if (s) { this._restoreSelection(); this.exec('foreColor', s.dataset.color); this._closePopover(); } });
			this.popover.appendChild(grid);
			this.popover.appendChild(h('div', { className: 'dfc-richtext-popover-row' }, custom, h('button', { type: 'button', className: 'dfc-btn dfc-btn-ghost', text: 'Remove colour', onClick: () => { this._restoreSelection(); this.exec('foreColor', 'inherit'); this._closePopover(); } })));
		} else if (kind === 'link') {
			const existing = this._closestLink();
			const input = h('input', { type: 'url', className: 'dfc-richtext-linkinput', placeholder: 'https://', value: existing ? existing.getAttribute('href') : '', 'aria-label': 'Link address' });
			const apply = () => {
				const url = input.value.trim();
				this._restoreSelection();
				if (!url) { this.exec('unlink'); this._closePopover(); return; }
				if (!/^(https?:\/\/|mailto:|tel:|\/)/i.test(url)) { input.classList.add('is-invalid'); return; }
				const sel = window.getSelection();
				if (existing) { existing.setAttribute('href', url); this._afterEdit('user'); }
				else if (sel && sel.isCollapsed) this.exec('insertHTML', `<a href="${escapeHtml(url)}"${o.linkTarget ? ` target="${o.linkTarget}" rel="noopener"` : ''}>${escapeHtml(url)}</a>`);
				else { this.exec('createLink', url); const a = this._closestLink(); if (a && o.linkTarget) { a.setAttribute('target', o.linkTarget); a.setAttribute('rel', 'noopener'); } }
				this._closePopover();
			};
			input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); apply(); } if (e.key === 'Escape') this._closePopover(); });
			this.popover.appendChild(h('div', { className: 'dfc-richtext-popover-row' }, input,
				h('button', { type: 'button', className: 'dfc-btn', text: existing ? 'Update' : 'Add link', onClick: apply }),
				existing ? h('button', { type: 'button', className: 'dfc-btn dfc-btn-ghost', text: 'Remove', onClick: () => { this._restoreSelection(); this.exec('unlink'); this._closePopover(); } }) : null));
			setTimeout(() => input.focus(), 0);
		}
	}
	_closePopover() { if (this.popover) { this.popover.hidden = true; this.popover.innerHTML = ''; } }
	_closestLink() {
		const sel = window.getSelection();
		if (!sel || sel.rangeCount === 0) return null;
		let n = sel.getRangeAt(0).commonAncestorContainer;
		if (n.nodeType === 3) n = n.parentNode;
		const a = n.closest && n.closest('a');
		return a && this.editor.contains(a) ? a : null;
	}

	// ------------------------------------------------------------ selection + state
	_saveSelection() {
		const sel = window.getSelection();
		if (sel && sel.rangeCount && this.editor.contains(sel.anchorNode)) this._range = sel.getRangeAt(0).cloneRange();
	}
	_restoreSelection() {
		if (!this._range) return;
		const sel = window.getSelection();
		try { sel.removeAllRanges(); sel.addRange(this._range); } catch (e) { /* stale range */ }
	}
	_refreshStates() {
		if (!this.buttons) return;
		Object.entries(this.buttons).forEach(([name, btn]) => {
			const def = ITEMS[name];
			if (def && def.state && btn.tagName === 'BUTTON') {
				let on = false; try { on = document.queryCommandState(def.state); } catch (e) { on = false; }
				btn.setAttribute('aria-pressed', on ? 'true' : 'false');
				btn.classList.toggle('is-active', on);
			}
			if (name === 'heading') {
				let block = ''; try { block = String(document.queryCommandValue('formatBlock') || '').toLowerCase(); } catch (e) { block = ''; }
				if (block && [...btn.options].some((op) => op.value === block)) btn.value = block; else btn.value = 'p';
			}
		});
	}

	// ------------------------------------------------------------ value + history
	get value() { return this._sourceMode ? RichText.sanitize(this.source.value, this._allowed(), this._sanOpts()) : RichText.sanitize(this.editor.innerHTML, this._allowed(), this._sanOpts()); }
	set value(html) {
		const previous = this.value;
		const clean = RichText.sanitize(html || '', this._allowed(), this._sanOpts());
		if (this._sourceMode) this.source.value = clean;
		this.editor.innerHTML = clean;
		this._history = { stack: [clean], index: 0 };
		this._valueChanged(clean, previous, 'api');
	}
	getText() { return this.editor.innerText || ''; }
	get isEmpty() { return !this.getText().trim() && !this.editor.querySelector('img'); }

	_afterEdit(source) {
		const html = this.editor.innerHTML;
		const hist = this._history;
		if (hist.stack[hist.index] !== html) {
			hist.stack = hist.stack.slice(0, hist.index + 1);
			hist.stack.push(html);
			if (hist.stack.length > 200) hist.stack.shift();
			hist.index = hist.stack.length - 1;
		}
		this._refreshStates();
		const value = this.value;
		const previous = this._lastValue === undefined ? RichText.sanitize(hist.stack[0], this._allowed(), this._sanOpts()) : this._lastValue;
		this._lastValue = value;
		this._valueChanged(value, previous, source);
	}
	undo() {
		const hist = this._history;
		if (hist.index <= 0) return this;
		hist.index -= 1; this.editor.innerHTML = hist.stack[hist.index]; this._caretToEnd(); this._refreshStates();
		const v = this.value; this._valueChanged(v, this._lastValue, 'user'); this._lastValue = v; return this;
	}
	redo() {
		const hist = this._history;
		if (hist.index >= hist.stack.length - 1) return this;
		hist.index += 1; this.editor.innerHTML = hist.stack[hist.index]; this._caretToEnd(); this._refreshStates();
		const v = this.value; this._valueChanged(v, this._lastValue, 'user'); this._lastValue = v; return this;
	}
	_caretToEnd() {
		const range = document.createRange(); range.selectNodeContents(this.editor); range.collapse(false);
		const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(range); this._range = range.cloneRange();
	}

	toggleSource(on) {
		const next = on === undefined ? !this._sourceMode : !!on;
		if (next === this._sourceMode) return this;
		if (next) {
			this.source.value = RichText.pretty(this.value);
			this.editor.hidden = true; this.source.hidden = false; this.source.focus();
		} else {
			if (this._sourceDirty) { this.editor.innerHTML = RichText.sanitize(this.source.value, this._allowed(), this._sanOpts()); this._sourceDirty = false; }
			this.source.hidden = true; this.editor.hidden = false; this.editor.focus();
		}
		this._sourceMode = next;
		if (this.buttons.source) { this.buttons.source.classList.toggle('is-active', next); this.buttons.source.setAttribute('aria-pressed', next ? 'true' : 'false'); }
		Object.entries(this.buttons).forEach(([n, b]) => { if (n !== 'source') b.disabled = next; });
		if (!next) this._afterEdit('user');
		return this;
	}

	focus() { (this._sourceMode ? this.source : this.editor).focus(); return this; }
	_allowed() { return this.options.allowedTags || ALLOWED; }
	/** Sanitiser options derived from the instance options (custom image URL schemes). */
	_sanOpts() { return { imageSrcPattern: this.options.imageSrcPattern }; }

	// ------------------------------------------------------------ sanitiser
	/**
	 * Whitelist sanitiser: keeps the tags/attributes in `allowed`, filters
	 * style declarations, drops scripts, event handlers and unknown URL
	 * schemes, and unwraps unknown elements (their text survives).
	 */
	static sanitize(html, allowed = ALLOWED, opts = {}) {
		const tpl = document.createElement('template');
		tpl.innerHTML = String(html || '');
		const walk = (node) => {
			[...node.childNodes].forEach((child) => {
				if (child.nodeType === 8) { child.remove(); return; }
				if (child.nodeType !== 1) return;
				const tag = child.tagName.toLowerCase();
				if (['script', 'style', 'iframe', 'object', 'embed', 'meta', 'link', 'title', 'head', 'svg', 'math', 'form', 'input', 'button', 'textarea', 'select'].includes(tag)) { child.remove(); return; }
				walk(child);
				if (!allowed[tag]) {
					// unwrap unknown elements; block-ish ones become paragraphs so text keeps its lines
					const blockish = ['section', 'article', 'header', 'footer', 'main', 'aside', 'nav', 'center', 'figure', 'figcaption', 'dl', 'dt', 'dd', 'address', 'html', 'body'].includes(tag);
					const repl = blockish ? document.createElement('p') : null;
					if (repl) { while (child.firstChild) repl.appendChild(child.firstChild); child.replaceWith(repl); }
					else { while (child.firstChild) child.parentNode.insertBefore(child.firstChild, child); child.remove(); }
					return;
				}
				[...child.attributes].forEach((attr) => {
					const name = attr.name.toLowerCase();
					if (!allowed[tag].includes(name)) { child.removeAttribute(attr.name); return; }
					if (name === 'href') { if (!/^(https?:\/\/|mailto:|tel:|\/|#)/i.test(attr.value.trim())) child.removeAttribute('href'); else if (child.getAttribute('target') === '_blank') child.setAttribute('rel', 'noopener'); }
					else if (name === 'src') { if (!(opts.imageSrcPattern || /^(data:image\/|https?:\/\/|\/)/i).test(attr.value.trim())) child.removeAttribute('src'); }
					else if (name === 'style') {
						if (opts.stripStyles && tag !== 'img') { child.removeAttribute('style'); return; }
						const kept = attr.value.split(';').map((d) => d.trim()).filter(Boolean).map((d) => { const i = d.indexOf(':'); return [d.slice(0, i).trim().toLowerCase(), d.slice(i + 1).trim()]; })
							.filter(([k, v]) => STYLE_ALLOW[k] && STYLE_ALLOW[k].test(v)).map(([k, v]) => k + ': ' + v).join('; ');
						if (kept) child.setAttribute('style', kept); else child.removeAttribute('style');
					} else if (name === 'target' && attr.value !== '_blank') child.removeAttribute('target');
					else if (name === 'class') { const kept = attr.value.split(/\s+/).filter((c) => /^dfc-/.test(c)).join(' '); if (kept) child.setAttribute('class', kept); else child.removeAttribute('class'); }
					else if (name === 'id') { if (!/^[a-z][\w-]*$/i.test(attr.value)) child.removeAttribute('id'); }
				});
				if (tag === 'div') { const p = document.createElement('p'); [...child.attributes].forEach((a) => p.setAttribute(a.name, a.value)); while (child.firstChild) p.appendChild(child.firstChild); child.replaceWith(p); }
				if (tag === 'img' && !child.getAttribute('src')) child.remove();
				if (tag === 'a' && !child.getAttribute('href')) { while (child.firstChild) child.parentNode.insertBefore(child.firstChild, child); child.remove(); }
			});
		};
		walk(tpl.content);
		return tpl.innerHTML;
	}

	/** Light pretty-printer for the source view. */
	static pretty(html) {
		return String(html || '').replace(/></g, '>\n<').replace(/\n(<\/(?:b|i|u|s|em|strong|a|span|font|code|sub|sup)>)/g, '$1').trim();
	}
}

register('richtext', RichText);
registerFieldType('richtext', {
	create: (container, field, value) => new RichText(container, { value: value || '', toolbar: field.toolbar || 'basic', readOnly: !!field.readonly, minHeight: field.minHeight || 160, placeholder: field.placeholder || '' }),
	getValue: (inst) => inst.value,
	setValue: (inst, v) => { inst.value = v; },
});
